import {
  REGION_AFRICA,
  REGION_ASIA,
  ZONE_LABELS,
} from '@/constants/travelProducts';
import { travelDaysBetween, usdToKsh } from '@/utils/travelPricing';
import {
  createPdfLayout,
  formatPdfCurrency,
  formatPdfDate,
  renderBenefitsTable,
  renderCompanyHeader,
  renderDocumentTitle,
  renderGrandTotal,
  renderInfoBox,
  renderIntermediaryStrip,
  renderInvoiceMeta,
  renderTableHeader,
  renderTableRow,
  renderValidityAndFooter,
} from '@/utils/quotePdfTheme';

function formatUsd(val) {
  return Number(val || 0).toFixed(2);
}

function formatMoney(n) {
  return Number(n || 0).toLocaleString('en-KE');
}

function summaryValue(quote, label) {
  const line = (quote.summaryLines || []).find((l) => l.label === label);
  return line?.value || '';
}

function resolveDestinationLabel(quote) {
  return summaryValue(quote, 'Destination')
    || quote.destinationLabel
    || quote.destinationCountry
    || quote.destination_country
    || 'N/A';
}

function resolveTravelPeriod(quote) {
  if (quote.startDate && quote.endDate) {
    const days = travelDaysBetween(quote.startDate, quote.endDate);
    const span = `${quote.startDate} to ${quote.endDate}`;
    return days ? `${span} (${days} days)` : span;
  }
  return summaryValue(quote, 'Travel period')
    || summaryValue(quote, 'Period')
    || 'N/A';
}

function resolveZoneLabel(quote) {
  return summaryValue(quote, 'Zone')
    || ZONE_LABELS[quote.zone]
    || quote.zone
    || 'N/A';
}

function resolveProductLabel(quote) {
  return quote.productLabel
    || summaryValue(quote, 'Product')
    || quote.plan_type
    || 'Travel Insurance';
}

function resolveTravellerCount(quote) {
  const fromSummary = summaryValue(quote, 'Travellers');
  if (fromSummary) return fromSummary;
  return String(quote.travellerCount || quote.traveller_count || 1);
}

const BENEFIT_MEDICAL = 'Medical expenses';
const BENEFIT_EVACUATION = 'Medical evacuation & repatriation';
const BENEFIT_CANCELLATION = 'Trip cancellation';
const BENEFIT_EFFECTS = 'Personal effects';

function standardBenefits(medical, evacuation, cancellation, effects, extra = []) {
  return [
    { label: BENEFIT_MEDICAL, limit: medical },
    { label: BENEFIT_EVACUATION, limit: evacuation },
    { label: BENEFIT_CANCELLATION, limit: cancellation },
    { label: BENEFIT_EFFECTS, limit: effects },
    ...extra,
  ];
}

const TRAVEL_BENEFIT_LIMITS = {
  [REGION_AFRICA]: standardBenefits(15000, 15000, 2000, 1500),
  [REGION_ASIA]: standardBenefits(15000, 15000, 2000, 1500),
  Europe: [
    ...standardBenefits(40000, 40000, 5000, 3000),
    { label: 'Delayed departure', limit: 300 },
  ],
  'Worldwide Basic': [
    ...standardBenefits(40000, 40000, 8000, 5000),
    { label: 'Personal liability', limit: 100000 },
  ],
  'Worldwide Premium': [
    ...standardBenefits(75000, 75000, 12000, 8000),
    { label: 'Hospital daily cash', limit: 50 },
  ],
};

export function resolveTravelBenefits(quote) {
  if (Array.isArray(quote.benefits) && quote.benefits.length) {
    return quote.benefits;
  }
  const region = quote.region
    || resolveProductLabel(quote).split(' — ')[0]
    || '';
  return TRAVEL_BENEFIT_LIMITS[region] || TRAVEL_BENEFIT_LIMITS[REGION_AFRICA];
}

/**
 * Build motor-style travel insurance quote PDF (jsPDF).
 */
export function buildTravelQuotePDF(doc, quote, options = {}) {
  const clientName = options.clientName || quote.customerName || quote.proposerName || 'N/A';
  const clientEmail = options.clientEmail || quote.customerEmail || quote.email_address || 'N/A';
  const agentName = options.agentName || 'N/A';
  const quoteId = quote.quoteId || '—';
  const invoiceNo = `INV-${String(quoteId).replace(/\D/g, '').padStart(6, '0').slice(-6) || quoteId}`;
  const productLabel = resolveProductLabel(quote);
  const grossUsd = Number(quote.grossUsd || quote.premium || 0);
  const fxRate = Number(quote.fxRate || quote.fx_rate || 130);
  const kshAmount = Number(quote.kshAmount || usdToKsh(grossUsd, fxRate));
  const benefits = resolveTravelBenefits(quote);

  const layout = createPdfLayout(doc);
  let y = renderCompanyHeader(doc, layout, { agentName });
  y = renderDocumentTitle(doc, layout, 'TRAVEL INSURANCE INVOICE', y);
  y = renderInvoiceMeta(doc, layout, { invoiceNo, clientName, clientEmail }, y);

  const boxH = 36;
  const colW = (layout.contentW - 6) / 2;
  const leftRows = [
    ['Name', clientName],
    ['Email', clientEmail],
    ['Quote ID', String(quoteId)],
  ];
  const rightRows = [
    ['Product', productLabel],
    ['Zone', resolveZoneLabel(quote)],
    ['Destination', resolveDestinationLabel(quote)],
    ['Travel period', resolveTravelPeriod(quote)],
    ['Travellers', resolveTravellerCount(quote)],
  ];
  renderInfoBox(doc, layout.margin, y, colW, boxH, 'CLIENT DETAILS', leftRows);
  renderInfoBox(doc, layout.margin + colW + 6, y, colW, boxH, 'TRIP DETAILS', rightRows);
  y += boxH + 6;

  y = renderIntermediaryStrip(doc, layout, agentName, y);
  y = renderTableHeader(doc, layout, 'INVOICE SUMMARY', 'Amount', y);
  y = renderTableRow(
    doc,
    layout,
    `Travel Insurance — ${productLabel}`,
    `USD ${formatUsd(grossUsd)}`,
    y,
  );
  y = renderTableRow(
    doc,
    layout,
    `FX conversion @ ${fxRate} USD/KSH`,
    `KES ${formatPdfCurrency(kshAmount)}`,
    y,
    { dim: true },
  );
  y = renderGrandTotal(doc, layout, kshAmount, y, {
    subtitle: `USD ${formatUsd(grossUsd)}`,
  });
  y = renderBenefitsTable(doc, layout, benefits, y, { limitHeader: 'Limit (USD)' });
  renderValidityAndFooter(doc, layout, y);
}

export function buildTravelQuoteEmailHtml(quote, customerName, options = {}) {
  const name = customerName || quote.customerName || 'Customer';
  const agentName = options.agentName || 'Pacis Insurance';
  const quoteId = quote.quoteId || '—';
  const invoiceNo = `INV-${String(quoteId).replace(/\D/g, '').padStart(6, '0').slice(-6) || quoteId}`;
  const productLabel = resolveProductLabel(quote);
  const grossUsd = formatUsd(quote.grossUsd || quote.premium);
  const ksh = formatMoney(quote.kshAmount || usdToKsh(quote.grossUsd, quote.fxRate));
  const fxRate = quote.fxRate || quote.fx_rate || 130;
  const dateStr = formatPdfDate();
  const benefits = resolveTravelBenefits(quote);

  const summaryRows = (quote.summaryLines || [])
    .map((line, i) => {
      const bg = i % 2 === 0 ? 'background:#FAFCFF;' : '';
      return `<tr style="border-bottom:1px solid #f1f5f9;${bg}">
        <td style="padding:6px 10px;font-size:11px;color:#64748B;">${line.label}</td>
        <td style="padding:6px 10px;font-size:11px;text-align:right;font-weight:600;color:#1E4276;">${line.value}</td>
      </tr>`;
    })
    .join('');

  const benefitRows = benefits
    .map((b, i) => {
      const bg = i % 2 === 0 ? 'background:#FAFCFF;' : '';
      return `<tr style="border-bottom:1px solid #f1f5f9;${bg}">
        <td style="padding:6px 10px;font-size:11px;font-weight:600;color:#1E4276;">${b.label}</td>
        <td style="padding:6px 10px;font-size:11px;text-align:right;font-weight:700;color:#00AEEF;">USD ${formatPdfCurrency(b.limit)}</td>
      </tr>`;
    })
    .join('');

  return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;padding:24px 0;"><tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(30,66,118,0.08);">
<tr><td style="padding:16px 20px;border-bottom:1px solid #e2e8f0;">
  <table width="100%"><tr>
    <td><div style="font-size:16px;font-weight:800;color:#1E4276;">Pacis Insurance</div>
    <div style="font-size:11px;color:#64748b;margin-top:2px;">With you every step of the way</div></td>
    <td style="text-align:right;font-size:9px;color:#64748b;line-height:1.5;">
      <div style="font-weight:700;color:#1E4276;">Pacis Insurance Company Limited</div>
      Pacis Centre, 4th Floor, off Waiyaki Way<br>P.O BOX 1870-00200, Nairobi<br>
      <span style="color:#00AEEF;font-weight:700;">INTERMEDIARY: ${agentName}</span>
    </td>
  </tr></table>
</td></tr>
<tr><td style="background:#1E4276;padding:10px 20px;">
  <table width="100%"><tr>
    <td style="font-size:12px;font-weight:800;color:#fff;letter-spacing:0.4px;">TRAVEL INSURANCE INVOICE</td>
    <td style="text-align:right;font-size:9px;color:rgba(255,255,255,0.75);">Date: ${dateStr}</td>
  </tr></table>
</td></tr>
<tr><td style="padding:16px 20px;">
  <p style="margin:0 0 10px;color:#1E4276;font-size:14px;">Dear ${name},</p>
  <p style="margin:0 0 14px;color:#475569;font-size:13px;line-height:1.5;">Please find your travel insurance quotation below.</p>
  <table width="100%" style="margin-bottom:12px;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
    <tr style="background:#EBF5FF;"><td colspan="2" style="padding:6px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;">Bill To</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;width:35%;">Invoice No.</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${invoiceNo}</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;">Product</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${productLabel}</td></tr>
    <tr><td style="padding:6px 10px;font-size:10px;color:#64748b;">Quote ref</td><td style="padding:6px 10px;font-size:11px;font-weight:700;color:#1E4276;">${quoteId}</td></tr>
  </table>
  ${summaryRows ? `<table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;margin-bottom:14px;">${summaryRows}</table>` : ''}
  <div style="font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;margin-bottom:6px;border-bottom:2px solid #1E4276;padding-bottom:4px;">Benefits &amp; limits (USD)</div>
  <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;margin-bottom:14px;">${benefitRows}</table>
  <p style="margin:0;font-size:20px;font-weight:800;color:#184679;">Premium: USD ${grossUsd} / KES ${ksh}</p>
  <p style="margin:6px 0 0;font-size:11px;color:#94A3B8;">Conversion @ ${fxRate} USD/KSH · Valid 30 days. Cover commences upon receipt of premium.</p>
</td></tr>
<tr><td style="padding:12px 20px;background:#FFFBEB;border-top:1px solid #FDE68A;font-size:10px;color:#92400e;">
  This is a computer-generated quotation and does not require a signature.
</td></tr>
</table></td></tr></table></body></html>`;
}

export async function openTravelQuotePdf(quote, clientName, options = {}) {
  if (!quote) return false;
  if (!window.jspdf?.jsPDF) {
    throw new Error('PDF library not loaded');
  }
  const { jsPDF: JsPDF } = window.jspdf;
  const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  buildTravelQuotePDF(doc, quote, {
    ...options,
    clientName: clientName || quote.customerName || quote.proposerName,
  });
  const blob = doc.output('bloburl');
  window.open(blob, '_blank');
  return true;
}
